import React, { useState, useEffect } from "react";
import {
  getGrupoEmpresa, asociarEmpresaAGrupo, desasociarEmpresaDeGrupo, crearEmpresa, eliminarEmpresa,
  GrupoEmpresaDetalle
} from "../../services/api";
import ConfirmarEliminacion from "../../components/ConfirmarEliminacion";

interface Props {
  grupoActivoId: number | null;
  nombreGrupo: string;
  usuarioId: number;
  token: string;
  onDatosCambiados?: () => void;
}

export default function GrupoGeneral({ grupoActivoId, nombreGrupo, usuarioId, token, onDatosCambiados }: Props) {
  const [detalle, setDetalle] = useState<GrupoEmpresaDetalle | null>(null);
  const [cargando, setCargando] = useState(true);
  const [empresaAAsociar, setEmpresaAAsociar] = useState<string>("");
  const [nombreNueva, setNombreNueva] = useState("");
  const [mostrarNueva, setMostrarNueva] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [mensajeOk, setMensajeOk] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [aEliminar, setAEliminar] = useState<{ empresaId: number; nombre: string } | null>(null);

  const cargar = async () => {
    if (!grupoActivoId) { setCargando(false); return; }
    setCargando(true);
    const data = await getGrupoEmpresa(grupoActivoId, usuarioId, token);
    setDetalle(data);
    setCargando(false);
  };

  useEffect(() => { cargar(); }, [grupoActivoId]);

  const mostrarError = (mensaje: string) => {
    setMensajeError(mensaje || "Ocurrió un error");
    setTimeout(() => setMensajeError(""), 4000);
  };

  const mostrarOk = (mensaje: string) => {
    setMensajeOk(mensaje);
    setTimeout(() => setMensajeOk(""), 2000);
  };

  const handleAsociar = async () => {
    if (!grupoActivoId || !empresaAAsociar) return;
    const res = await asociarEmpresaAGrupo(grupoActivoId, Number(empresaAAsociar), usuarioId, token);
    if (res.ok) {
      setEmpresaAAsociar("");
      await cargar();
      onDatosCambiados?.();
      mostrarOk("Empresa asociada");
    } else {
      mostrarError(res.mensaje);
    }
  };

  const handleCrear = async () => {
    if (!grupoActivoId || !nombreNueva.trim()) return;
    setGuardando(true);
    const res = await crearEmpresa(usuarioId, { nombre: nombreNueva.trim(), grupoEmpresaId: grupoActivoId }, token);
    setGuardando(false);
    if (res.ok) {
      setNombreNueva("");
      setMostrarNueva(false);
      await cargar();
      onDatosCambiados?.();
      mostrarOk("Empresa creada");
    } else {
      mostrarError(res.mensaje);
    }
  };

  const handleDesasociar = async (empresaId: number) => {
    if (!grupoActivoId) return;
    const res = await desasociarEmpresaDeGrupo(grupoActivoId, empresaId, usuarioId, token);
    if (res.ok) {
      await cargar();
      onDatosCambiados?.();
      mostrarOk("Empresa desasociada");
    } else {
      mostrarError(res.mensaje);
    }
  };

  const handleEliminar = async () => {
    if (!aEliminar) return;
    const res = await eliminarEmpresa(aEliminar.empresaId, usuarioId, token);
    setAEliminar(null);
    if (res.ok) {
      await cargar();
      onDatosCambiados?.();
      mostrarOk("Empresa eliminada");
    } else {
      mostrarError(res.mensaje);
    }
  };

  if (!grupoActivoId) {
    return (
      <div style={styles.container}>
        <div style={styles.vacio}>No tenés un grupo activo seleccionado.</div>
      </div>
    );
  }

  if (cargando) {
    return <div style={styles.container}><div style={styles.vacio}>Cargando...</div></div>;
  }

  if (!detalle) {
    return <div style={styles.container}><div style={styles.vacio}>No se pudo cargar el grupo.</div></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.titulo}>{nombreGrupo || detalle.nombre}</span>
      </div>

      {mensajeOk && <div style={styles.mensajeOk}>✓ {mensajeOk}</div>}
      {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}

      <div style={styles.seccion}>
        <h4 style={styles.subTitulo}>Empresas que integran el grupo</h4>
        {detalle.empresas.length === 0 && (
          <div style={styles.vacio}>No hay empresas asociadas todavía.</div>
        )}
        {detalle.empresas.map(e => (
          <div key={e.empresaId} style={styles.filaEmpresa}>
            <span style={{ flex: 1 }}>{e.nombre}</span>
            <button style={styles.btnAccion} onClick={() => handleDesasociar(e.empresaId)}>Desasociar</button>
            <button style={styles.btnEliminar} onClick={() => setAEliminar({ empresaId: e.empresaId, nombre: e.nombre })}>
              Eliminar
            </button>
          </div>
        ))}
      </div>

      <div style={styles.seccion}>
        <h4 style={styles.subTitulo}>Asociar empresa existente</h4>
        {detalle.empresasDisponibles.length === 0 ? (
          <div style={styles.vacio}>No hay empresas sin grupo disponibles para asociar.</div>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <select style={styles.input} value={empresaAAsociar} onChange={e => setEmpresaAAsociar(e.target.value)}>
              <option value="">— Seleccionar empresa —</option>
              {detalle.empresasDisponibles.map(e => (
                <option key={e.empresaId} value={e.empresaId}>{e.nombre}</option>
              ))}
            </select>
            <button style={styles.btnPrimario} onClick={handleAsociar} disabled={!empresaAAsociar}>Asociar</button>
          </div>
        )}
      </div>

      <div style={styles.seccion}>
        <h4 style={styles.subTitulo}>Nueva empresa</h4>
        {!mostrarNueva ? (
          <button style={styles.btnPrimario} onClick={() => setMostrarNueva(true)}>+ Nueva empresa</button>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={styles.input}
              placeholder="Nombre de la empresa"
              value={nombreNueva}
              onChange={e => setNombreNueva(e.target.value)}
            />
            <button style={styles.btnPrimario} onClick={handleCrear} disabled={guardando || !nombreNueva.trim()}>
              {guardando ? "Creando..." : "Crear"}
            </button>
            <button style={styles.btnSecundario} onClick={() => { setMostrarNueva(false); setNombreNueva(""); }}>
              Cancelar
            </button>
          </div>
        )}
      </div>

      <div style={styles.notaBox}>
        Próximamente: configuración de qué objetos comparten las empresas del grupo
        (plan de cuentas, monedas y otros auxiliares — íntegra, parcial o independiente).
      </div>

      {aEliminar && (
        <ConfirmarEliminacion
          titulo={`Eliminar empresa "${aEliminar.nombre}"`}
          nombreEsperado={aEliminar.nombre}
          advertencia="Se borra la empresa y sus asignaciones de usuarios/roles. Si tiene plan de cuentas, monedas o cotizaciones cargados, no se va a poder eliminar."
          onConfirmar={handleEliminar}
          onCancelar={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: 24 },
  header: { marginBottom: 18, borderBottom: "1px solid #f0f0f0", paddingBottom: 14 },
  titulo: { fontSize: 17, fontWeight: 700, color: "#1a1a2e" },
  seccion: { marginBottom: 20 },
  subTitulo: { fontSize: 12, fontWeight: 700, color: "#999", textTransform: "uppercase" as const, letterSpacing: "0.5px", marginBottom: 10 },
  filaEmpresa: { display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", backgroundColor: "#f8f9fa", borderRadius: 8, marginBottom: 6, fontSize: 13, color: "#333" },
  vacio: { padding: 20, textAlign: "center" as const, color: "#bbb", fontSize: 13 },
  notaBox: { fontSize: 12, color: "#888", backgroundColor: "#fdf6e8", border: "1px solid #f0e0b0", borderRadius: 8, padding: "12px 14px", lineHeight: 1.5 },
  input: { flex: 1, padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, boxSizing: "border-box" as const, outline: "none" },
  btnPrimario: { padding: "8px 16px", backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" as const },
  btnSecundario: { padding: "8px 16px", backgroundColor: "#f4f6f8", color: "#555", border: "1px solid #ddd", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  btnAccion: { padding: "5px 10px", backgroundColor: "#fff", color: "#555", border: "1px solid #ddd", borderRadius: 6, fontSize: 12, cursor: "pointer" },
  btnEliminar: { padding: "5px 10px", backgroundColor: "#fff0f0", color: "#e74c3c", border: "1px solid #f5c6c6", borderRadius: 6, fontSize: 12, cursor: "pointer" },
  mensajeOk: { backgroundColor: "#e8f8f0", color: "#27ae60", padding: "10px 16px", borderRadius: 8, fontSize: 13, marginBottom: 12 },
  mensajeError: { backgroundColor: "#fff0f0", color: "#e74c3c", padding: "10px 16px", borderRadius: 8, fontSize: 13, marginBottom: 12 },
};
